import { HelpCircle } from '@ethsign/icons';

import { DatePicker, Input, Label, Switch, Textarea, TimePicker, Tooltip } from '@ethsign/ui';

import dayjs from 'dayjs';
import React, { ReactNode } from 'react';

import { Card, PasswordInput } from '@/components';
import { EmailInput } from '@/components/TextField/EmailInput';
import { cn } from '@/lib/utils';
import { IContact, SendType } from '@/types';
import { dateFormat, isBeforeToday } from '@/utils';

import { PageData } from '../PageCtrl';

import { SignersEditTable } from './InviteSigners';

interface IReviewAndSendProps {
  data: {
    expireDate?: Date;
    password: string;
    enableExpireDate: boolean;
    enablePassword: boolean;
    sendType: string;
    expireTimeError?: boolean;
    recipients: IContact[];
    contractName: string;
    extraMessage: string;
    viewers: IContact[];
  };
  setData: (data: Partial<PageData>) => void;
}

const contractEncryptionTooltip = (
  <div className={'flex gap-[4px] items-center'}>
    Contract Encryption
    <Tooltip
      content={
        <div className={'text-xs font-medium text-gray-700'}>
          EthSign stores all contracts publicly on decentralized blockchain. <br />
          We strongly recommend set a password for the contract.
        </div>
      }
    >
      <span>
        <HelpCircle size={16} color={'#667085'} className="cursor-pointer" />
      </span>
    </Tooltip>
  </div>
);

const SettingCard = ({
  extra,
  title,
  description,
  children,
  className
}: {
  extra?: ReactNode;
  title?: ReactNode;
  description?: string;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('flex-1 py-5 px-6', className)}>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-sm text-gray-900">{title}</div>
          <div className="text-xs text-gray-400 mt-[4px]">{description}</div>
        </div>
        {extra}
      </div>
      <div className={'pt-4'}>{children}</div>
    </div>
  );
};

const ShareModelReview = ({
  data: { password, enablePassword, contractName, expireDate, expireTimeError },
  setData
}: IReviewAndSendProps) => {
  return (
    <div className="space-y-4 w-[800px] m-auto">
      <div className={'flex-1'}>
        <Card title={'Contract Settings'} subTitle={'Set up your notification and encryption method.'}>
          <SettingCard title={'Contract Name'} description={'Contract name should not exceed 200 characters.'}>
            <Input
              type="text"
              placeholder={'An interesting contract name'}
              value={contractName}
              onChange={(e) => setData({ contractName: e.target.value?.slice(0, 200) })}
            />
          </SettingCard>
          <SettingCard
            title={contractEncryptionTooltip}
            description={'Set contract password to encrypt the file. '}
            extra={<Switch checked={enablePassword} onCheckedChange={(v) => setData({ enablePassword: v })} />}
          >
            <PasswordInput
              value={password}
              onChange={(e) => setData({ password: e.target.value })}
              disabled={!enablePassword}
            />
          </SettingCard>
        </Card>
        <Card title={'Shareable Link'} className={'mt-6'}>
          <div>
            <SettingCard
              title={'Complete the contract by*'}
              description={'The contract link will not be accessible after this date.'}
            >
              <DatePicker
                placeholder={'DD/MM/YY HH:MM:SS'}
                format={'DD/MM/YY HH:mm:ss'}
                className={'w-full'}
                value={expireDate}
                onChange={(v) => {
                  if (expireDate) {
                    const hour = dayjs(expireDate).hour();
                    const minute = dayjs(expireDate).minute();
                    const second = dayjs(expireDate).second();
                    setData({ expireDate: dayjs(v).hour(hour).minute(minute).second(second).toDate() });
                  } else {
                    setData({ expireDate: dayjs(v).add(1, 'day').subtract(1, 'second').toDate() });
                  }
                }}
                calendarProps={{
                  disabled: (date: Date) => isBeforeToday(date),
                  footer: (
                    <div className={'flex py-4 items-center justify-between mt-2 border-t border-gray-200'}>
                      <div className={'text-gray-700 text-md font-medium mr-[60px]'}>Contract End Time</div>
                      <TimePicker
                        className={'w-[120px]'}
                        value={expireDate!}
                        onChange={(v) => setData({ expireDate: v })}
                      />
                    </div>
                  )
                }}
              />
              <div className={'text-gray-500 text-sm mt-2'}>
                {expireDate && (
                  <div>
                    Signers will not be able to sign on this contract after{' '}
                    {dateFormat(expireDate, 'YYYY/MM/DD HH:mm:ss')}.
                  </div>
                )}
                <div className={'font-medium'}>
                  The contract will be marked as completed and stored on chain after this date.
                </div>
              </div>
              {expireTimeError && (
                <div className="text-sm font-normal text-red-500 mt-[6px]">
                  Please select expire date that is after today.
                </div>
              )}
            </SettingCard>
          </div>
        </Card>
      </div>
    </div>
  );
};

const NormalModeReview = ({
  data: { expireDate, password, enableExpireDate, enablePassword, recipients, contractName, extraMessage, viewers },
  setData
}: IReviewAndSendProps) => {
  return (
    <div className="space-y-[40px]">
      <Card title={'Contract Recipients'} subTitle={'Review your signers'}>
        <SignersEditTable retainLastOne data={recipients} onChange={(v) => setData({ recipients: v })} />
      </Card>
      <div className={'flex gap-8'}>
        <Card
          title={'Contract Settings'}
          subTitle={'Once submitted, these settings cannot be changed'}
          className={'flex-1'}
        >
          <SettingCard title={'Contract Name'} description={'Contract name should not exceed 200 characters.'}>
            <Input
              type="text"
              placeholder={'An interesting contract name'}
              value={contractName}
              onChange={(e) => setData({ contractName: e.target.value?.slice(0, 200) })}
            />
          </SettingCard>
          <SettingCard
            title={contractEncryptionTooltip}
            description={'All recipients need this password to decrypt and view the contract'}
            extra={<Switch checked={enablePassword} onCheckedChange={(v) => setData({ enablePassword: v })} />}
          >
            <PasswordInput
              value={password}
              onChange={(e) => setData({ password: e.target.value })}
              disabled={!enablePassword}
            />
          </SettingCard>
          <SettingCard
            title={'Set Contract Expiration'}
            description={'The contract becomes read-only after this date'}
            extra={
              <Switch
                checked={enableExpireDate}
                onCheckedChange={(v) => {
                  setData({ enableExpireDate: v });
                }}
              />
            }
          >
            <DatePicker
              placeholder={'DD/MM/YY HH:MM:SS'}
              format={'DD/MM/YY HH:mm:ss'}
              disabled={!enableExpireDate}
              className={'w-full'}
              value={expireDate}
              onChange={(v) => {
                if (expireDate) {
                  const hour = dayjs(expireDate).hour();
                  const minute = dayjs(expireDate).minute();
                  const second = dayjs(expireDate).second();
                  setData({ expireDate: dayjs(v).hour(hour).minute(minute).second(second).toDate() });
                } else {
                  setData({ expireDate: dayjs(v).add(1, 'day').subtract(1, 'second').toDate() });
                }
              }}
              calendarProps={{
                disabled: (date: Date) => isBeforeToday(date),
                footer: (
                  <div className={'flex py-4 items-center justify-between mt-2 border-t border-gray-200'}>
                    <div className={'text-gray-700 text-md font-medium mr-[60px]'}>Contract End Time</div>
                    <TimePicker
                      className={'w-[120px]'}
                      value={expireDate!}
                      onChange={(v) => {
                        setData({ expireDate: v });
                      }}
                    />
                  </div>
                )
              }}
            />
          </SettingCard>
        </Card>
        <div className="space-y-6 flex-1">
          <Card
            title={'Contract Viewer'}
            subTitle={'Viewers added will have access to the contract content.'}
            className={'flex-1'}
          >
            <div className={'px-6 py-5'}>
              <EmailInput
                data={viewers}
                onChange={(v) => {
                  setData({ viewers: v });
                }}
              />
            </div>
          </Card>
          <Card
            title={'Sender’s Message'}
            subTitle={
              'Send a customized message to contract recipients. This message will not be stored anywhere or encrypted.'
            }
            className={'flex-1'}
          >
            <div className={'px-6 py-5'}>
              <Textarea
                className={'w-full h-[328px] border-gray-300 rounded-lg'}
                placeholder={'You can use this field as a password hint. “The password is the time we first met”.'}
                value={extraMessage}
                onChange={(e) => {
                  setData({ extraMessage: e.target.value?.slice(0, 1000) });
                }}
              />
              <Label className={'text-gray-500'}>Character remaining: {1000 - (extraMessage?.length || 0)}</Label>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ReviewAndSend = (props: IReviewAndSendProps) => {
  return props.data.sendType === SendType.link ? <ShareModelReview {...props} /> : <NormalModeReview {...props} />;
};
